import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

interface Session {
  user_id: string
  tenant_id: string
  email: string
  is_demo: boolean
}

interface SessionContextValue {
  session: Session | null
  loading: boolean
  signout: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue>({
  session: null,
  loading: true,
  signout: async () => {},
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('echo_session')
    if (!token) {
      setLoading(false)
      return
    }
    fetch('/api/auth/session', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then((data: unknown) => setSession(data as Session | null))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const signout = async () => {
    const token = localStorage.getItem('echo_session')
    if (token) {
      await fetch('/api/auth/signout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {})
      localStorage.removeItem('echo_session')
    }
    setSession(null)
  }

  return (
    <SessionContext.Provider value={{ session, loading, signout }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  return useContext(SessionContext)
}
